import React, { useState, useEffect } from "react";
import { PickleballBall } from "./SportIcons";
import LoginFlow from "./auth/LoginFlow";
import AdminView from "./views/AdminView";
import PlayerView from "./views/PlayerView";
import SuperAdminView from "./views/SuperAdminView";
import { useAppUpdate } from "./useAppUpdate";
import LandingPage from "./LandingPage";
import IntroScreen, { INTRO_SEEN_KEY } from "./components/IntroScreen";
import PrivacyPage from "./PrivacyPage";
import ClubsLandingPage from "./ClubsLandingPage";
import { AuthSession } from "./types";
import { db, app, clubSignInFn, signInWithClubToken, refreshClaimsFn } from "./firebase";
import { registerPasskey } from "./auth/passkey";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged, signOut } from "firebase/auth";
import { isNative } from "./nativePush";

async function ensureAuth(): Promise<void> {
  const auth = getAuth(app);
  if (auth.currentUser) return;
  await signInAnonymously(auth);
  await new Promise<void>((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => { if (u) { unsub(); resolve(); } });
  });
}

// ── iOS PWA Install Banner ────────────────────────────────────
function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInPWA = window.matchMedia("(display-mode: standalone)").matches
      || (window.navigator as any).standalone === true;
    const dismissed = localStorage.getItem("spinloop_pwa_dismissed");
    // Natiiviappissa asennuskehotus on absurdi - appi on jo asennettu kaupasta
    if (isIOS && !isInPWA && !dismissed && !isNative()) setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: "#163b68", color: "#fff",
      padding: "16px 20px calc(28px + var(--sl-safe-bottom))",
      boxShadow: "0 -4px 24px rgba(0,0,0,0.3)",
      fontFamily: "'Sora', sans-serif",
    }}>
      <div style={{
        position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)",
        width: 0, height: 0,
        borderLeft: "10px solid transparent", borderRight: "10px solid transparent",
        borderTop: "10px solid #163b68",
      }} />
      <button
        onClick={() => { setVisible(false); localStorage.setItem("spinloop_pwa_dismissed", "1"); }}
        style={{ position: "absolute", top: 12, right: 16, background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 20, cursor: "pointer", padding: 0 }}
      >
        ×
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <img src="/logo192.png" alt="Spinloop" style={{ width: 48, height: 48, borderRadius: 12, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>
            Asenna Spinloop kotinäytölle
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
            Paina <strong>⬆️ Jaa</strong> → <strong>"Lisää kotinäytölle"</strong>
            <br />Saat push-ilmoitukset ja nopeamman käynnistyksen
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Invite acceptance screen ──────────────────────────────────
interface InviteData {
  token: string;
  clubId: string;
  playerId: string;
  playerName: string;
  email: string;
  clubName: string;
  // Tapahtumakutsu (functions/src/eventInvite.ts). Puuttuu klubikutsusta.
  eventId?: string;
  eventVenue?: string;
  eventDatetime?: string;
  // "verify" = pelaajan itse pyytämä vahvistuslinkki (requestVerifyLink).
  kind?: string;
}

/**
 * Kutsulinkin ruutu. Klubikutsussa yksi nappi; tapahtumakutsussa kaksi,
 * Tulen ja En pääse. Linkin avaaminen ei vielä kirjaa mitään — vasta
 * napin painallus, koska sähköpostin skannerit avaavat linkkejä
 * ennakkoon (docs/ideat.md E5).
 */
function InviteScreen({ invite, onAccept, onDecline }: {
  invite: InviteData;
  onAccept: (going?: boolean) => void;
  onDecline: () => void;
}) {
  const isEvent = !!invite.eventId;
  const when = invite.eventDatetime ? new Date(invite.eventDatetime) : null;
  const whenStr = when && !isNaN(when.getTime())
    ? when.toLocaleString("fi-FI", { weekday: "long", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";
  const [busy, setBusy] = useState(false);
  const answer = (going?: boolean) => { if (busy) return; setBusy(true); onAccept(going); };
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(160deg, #0d2540 0%, #163b68 100%)",
      padding: 24, fontFamily: "'Sora', sans-serif",
    }}>
      <div style={{
        background: "#fff", borderRadius: 20, padding: "36px 28px",
        maxWidth: 400, width: "100%",
        boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
        textAlign: "center",
      }}>
        <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><PickleballBall size={56} /></div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#163b68", fontFamily: "'Rajdhani', sans-serif", marginBottom: 6 }}>
          {invite.clubName}
        </div>
        <div style={{ fontSize: 14, color: "#8aa0b5", marginBottom: 24 }}>
          {isEvent
            ? "Sinut on kutsuttu pelaamaan"
            : invite.kind === "verify"
              ? "Vahvista kirjautumisesi. Sen jälkeen voit kirjoittaa tapahtumien viesteihin."
              : "Sinulle on lähetetty kutsu pelaajaksi"}
        </div>
        {isEvent && (
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1a2b3c", marginBottom: 4 }}>
            {invite.eventVenue || "Tapahtuma"}
          </div>
        )}
        {isEvent && whenStr && (
          <div style={{ fontSize: 14, color: "#163b68", marginBottom: 20 }}>📅 {whenStr}</div>
        )}
        <div style={{
          background: "#f5f7fa", borderRadius: 12, padding: "16px 20px",
          marginBottom: 28, border: "1px solid #e3e8ef",
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1a2b3c" }}>{invite.playerName}</div>
          <div style={{ fontSize: 13, color: "#8aa0b5", marginTop: 4 }}>{invite.email}</div>
        </div>
        {isEvent ? (
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <button onClick={() => answer(true)} disabled={busy} style={{
              flex: 1, padding: "14px 0", background: "linear-gradient(135deg, #163b68, #1a56db)",
              color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
            }}>
              ✅ Tulen
            </button>
            <button onClick={() => answer(false)} disabled={busy} style={{
              flex: 1, padding: "14px 0", background: "#fff", color: "#163b68",
              border: "1px solid #163b68", borderRadius: 12, fontSize: 15, fontWeight: 700,
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
            }}>
              En pääse
            </button>
          </div>
        ) : (
          <button
            onClick={() => answer()}
            disabled={busy}
            style={{
              width: "100%", padding: "14px 0",
              background: "linear-gradient(135deg, #163b68, #1a56db)",
              color: "#fff", border: "none", borderRadius: 12,
              fontSize: 15, fontWeight: 700, cursor: busy ? "wait" : "pointer",
              marginBottom: 10, opacity: busy ? 0.6 : 1,
            }}
          >
            ✅ {invite.kind === "verify" ? "Vahvista" : "Hyväksy kutsu"}
          </button>
        )}
        <button
          onClick={onDecline}
          style={{
            width: "100%", padding: "12px 0",
            background: "none", color: "#8aa0b5",
            border: "1px solid #e3e8ef", borderRadius: 12,
            fontSize: 13, cursor: "pointer",
          }}
        >
          Ei kiitos
        </button>
      </div>
    </div>
  );
}

const SESSION_KEY = "spinloop_player_session";


/**
 * Kehotus ladata uusi versio. Ei modaali eika automaattinen uudelleenlataus:
 * kayttaja voi olla kesken ilmoittautumisen tai tuloksen kirjaamisen, eika
 * sita saa keskeyttaa. Palkki jaa nakyviin kunnes siihen tartutaan.
 */
function UpdateBanner() {
  const { updateReady, reload } = useAppUpdate();
  if (!updateReady) return null;
  return (
    <div style={{
      position: "fixed", left: 12, right: 12, bottom: "calc(12px + var(--sl-safe-bottom))", zIndex: 9000,
      background: "#163b68", color: "#fff", borderRadius: 12,
      padding: "12px 14px", display: "flex", alignItems: "center", gap: 12,
      boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      fontFamily: "'Inter', sans-serif", maxWidth: 520, margin: "0 auto",
    }}>
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.45 }}>
        Uusi versio on saatavilla.
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
          Selaimessa on vanha versio Spinloopista.
        </div>
      </div>
      <button onClick={reload} style={{
        background: "#f4a426", color: "#0f2744", border: "none",
        borderRadius: 8, padding: "9px 14px", fontWeight: 800,
        fontSize: 13, cursor: "pointer", flexShrink: 0,
        fontFamily: "'Inter', sans-serif",
      }}>
        Päivitä
      </button>
    </div>
  );
}

function SpinloopApp() {
  const [session, setSession]           = useState<AuthSession | null>(() => {
    // Palautetaan tallennettua pelaajasessio jos sellainen on
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (saved) return JSON.parse(saved) as AuthSession;
    } catch {}
    return null;
  });
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  // Natiiviappissa ei nayteta laskeutumissivua: se on verkon markkinointisivu,
  // jossa puhutaan PWA:sta ja kotivalikkoon asentamisesta. Kaupassa se luetaan
  // "thin web wrapperiksi" ja on kayttajalle harhaanjohtava - appi on jo asennettu.
  // Kaupasta asennetun sovelluksen ensimmäinen käynnistys: esittely kerran
  // ennen kirjautumista (src/components/IntroScreen.tsx). Selaimessa
  // esikatselu osoitteella ?intro=1.
  const [showIntro, setShowIntro] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get("intro") === "1") return true;
      if (!isNative()) return false;
      if (localStorage.getItem(SESSION_KEY)) return false;
      return !localStorage.getItem(INTRO_SEEN_KEY);
    } catch { return false; }
  });
  const [showLanding, setShowLanding]   = useState(() => {
    if (isNative()) return false;
    if (localStorage.getItem(SESSION_KEY)) return false;
    // Kutsulinkillä tullut tietää jo mitä on hakemassa.
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("club") || q.get("invite")) return false;
    } catch { /* ei searchParams-tukea — näytetään sivu */ }
    return true;
  });
  const [invite, setInvite]             = useState<InviteData | null>(null);
  const [inviteChecked, setInviteChecked] = useState(false);
  // Tapahtumakutsun sähköposti linkittää muotoon ?club=KOODI. Ilman tätä
  // linkki avasi laskeutumissivun, joka markkinoi PWA-asennusta ja jätti
  // klubikoodin naputeltavaksi käsin — vaikka se oli samassa viestissä.
  const [linkClubCode] = useState<string | null>(() => {
    try {
      const c = new URLSearchParams(window.location.search).get("club");
      return c ? c.trim().toUpperCase() : null;
    } catch { return null; }
  });

  // Tallennettu sessio kelpaa vain jos Firebase-istunnolla on saman
  // klubin oikeudet. Ennen palvelinpuolen kirjautumista sessioita
  // syntyi pelkän anonyymin tunnistautumisen varaan; sellaisella ei
  // enää pääse lukemaan mitään, joten se ohjataan kirjautumaan
  // uudelleen sen sijaan että näkymä jäisi tyhjäksi virheineen.
  useEffect(() => {
    if (session?.role !== "player" && session?.role !== "organizer") return;
    const auth = getAuth(app);
    let refreshed = false;
    onAuthStateChanged(auth, async (user) => {
      if (!user) { ensureAuth().catch(() => {}); return; }
      try {
        const token = await user.getIdTokenResult();
        if (token.claims.clubId !== session.clubId) {
          localStorage.removeItem(SESSION_KEY);
          setSession(null);
          return;
        }
        // Rooli taustalla ajan tasalle: jos admin on nimennyt pelaajan
        // järjestäjäksi (tai poistanut roolin), muutos näkyy tällä
        // avauksella ilman uutta kirjautumista. Vain varmennetulle
        // istunnolle — PIN-istunnolle palvelin ei myönnä mitään uutta.
        if (refreshed || token.claims.verified !== true) return;
        refreshed = true;
        const res = await refreshClaimsFn({});
        if (res.data.token) await signInWithClubToken(res.data.token);
        const role = res.data.role === "organizer" ? "organizer" : "player";
        setSession(prev => {
          if (!prev || prev.role === role) return prev;
          const next = { ...prev, role } as AuthSession;
          localStorage.setItem(SESSION_KEY, JSON.stringify(next));
          return next;
        });
      } catch { /* verkkovirhe — annetaan session olla */ }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tarkista invite-token URL:sta ──────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite");
    if (!token) { setInviteChecked(true); return; }

    ensureAuth().then(() => getDoc(doc(db, "playerInvites", token))).then(snap => {
      if (!snap.exists()) { setInviteChecked(true); return; }
      const d = snap.data();
      if (d.used || new Date(d.expiresAt) < new Date()) {
        // Vanhentunut tai jo käytetty — jatketaan normaalisti
        setInviteChecked(true);
        return;
      }
      setInvite({
        token,
        clubId: d.clubId,
        playerId: d.playerId,
        playerName: d.playerName,
        email: d.email,
        clubName: d.clubName,
        eventId: d.eventId,
        eventVenue: d.eventVenue,
        eventDatetime: d.eventDatetime,
        kind: d.kind,
      });
      setInviteChecked(true);
    }).catch(() => setInviteChecked(true));
  }, []);

  if (window.location.pathname === "/privacy") {
    return <PrivacyPage />;
  }

  if (window.location.pathname === "/clubs") {
    return <ClubsLandingPage />;
  }

  function handleLogout() {
    localStorage.removeItem("spinloop_club_id");
    localStorage.removeItem("spinloop_club_code");
    localStorage.removeItem("spinloop_club_name");
    localStorage.removeItem("spinloop_name");
    localStorage.removeItem(SESSION_KEY);
    // Klubin oikeudet ovat nyt Firebase-istunnossa, eivät pelkässä
    // React-tilassa, joten uloskirjautumisen pitää myös purkaa istunto.
    signOut(getAuth(app)).catch(() => undefined);
    setSession(null);
    setShowSuperAdmin(false);
  }

  function handleLoginSuccess(s: AuthSession) {
    // Tallenna pelaajan ja järjestäjän sessio — admin-sessioita ei
    // tallenneta (ne vaativat PIN:n aina)
    if (s.role === "player" || s.role === "organizer") {
      localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    }
    setSession(s);
    setShowSuperAdmin(false);
  }

  async function handleInviteAccept(going?: boolean) {
    if (!invite) return;
    // Kutsutoken on itsessään todiste. Palvelin tarkistaa sen, merkitsee
    // käytetyksi ja myöntää tokenin, jonka claimit avaavat klubin
    // dokumentit — pelkkä anonyymi kirjautuminen ei enää riitä.
    let role: "player" | "organizer" = "player";
    let signedIn = false;
    try {
      const res = await clubSignInFn({ inviteToken: invite.token });
      if (res.data.token) {
        await signInWithClubToken(res.data.token);
        signedIn = true;
      }
      if (res.data.role === "organizer") role = "organizer";
    } catch {
      await ensureAuth();
    }
    // Kutsulinkki on varmennettu kirjautuminen, joten tässä istunnossa
    // rekisteröity passkey merkitään varmennetuksi ja tuo jatkossa myös
    // järjestäjän roolin. Taustalla: epäonnistuminen ei estä sisäänpääsyä.
    if (signedIn) {
      registerPasskey(invite.playerId, invite.playerName).catch(() => undefined);
    }
    // Tapahtumakutsun vastaus. Sama kirjoitus kuin pelaajan omassa
    // ilmoittautumisessa (PlayerView setAttendance): source "player"
    // erottaa vastanneen kutsutusta, joka ei ole vielä vastannut, ja
    // laukaisee ylläpitäjän ilmoituksen kun pelaaja tulee mukaan.
    if (signedIn && invite.eventId && going !== undefined) {
      try {
        const ref = doc(db, "clubs", invite.clubId, "events", invite.eventId, "participants", invite.playerId);
        const existing = (await getDoc(ref)).exists();
        await setDoc(ref, {
          playerId: invite.playerId,
          displayName: invite.playerName,
          confirmed: going,
          source: "player",
          ...(existing ? {} : { paid: false, createdAt: new Date().toISOString() }),
          updatedAt: new Date().toISOString(),
        }, { merge: true });
      } catch (err) {
        console.error("Kutsun vastaus epäonnistui", err);
      }
    }
    // Tallenna tiedot localStorageen — LoginFlow käyttää niitä
    localStorage.setItem("spinloop_club_id",   invite.clubId);
    localStorage.setItem("spinloop_club_name", invite.clubName);
    localStorage.setItem("spinloop_name",      invite.playerName);
    // Luo sessio suoraan (kutsu on autentikointi)
    const newSession: AuthSession = {
      clubId:   invite.clubId,
      clubCode: "",
      clubName: invite.clubName,
      role,
      playerId: invite.playerId,
      playerName: invite.playerName,
      ...(invite.eventId ? { eventId: invite.eventId } : {}),
    };
    // Tallenna sessio pysyvästi
    localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
    setInvite(null);
    setSession(newSession);
    // Siivoa URL
    window.history.replaceState({}, "", window.location.pathname);
  }

  function handleInviteDecline() {
    setInvite(null);
    window.history.replaceState({}, "", window.location.pathname);
  }

  if (!inviteChecked) {
    // Pieni latausruutu ennen kuin tiedetään onko invite
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg, #0d2540 0%, #163b68 100%)",
      }}>
        <div style={{ fontSize: 32 }}>⏳</div>
      </div>
    );
  }

  if (invite) {
    return <InviteScreen invite={invite} onAccept={handleInviteAccept} onDecline={handleInviteDecline} />;
  }

  if (showSuperAdmin) {
    return <SuperAdminView onLogout={() => setShowSuperAdmin(false)} />;
  }

  return (
    <>
      <UpdateBanner />
      <IOSInstallBanner />
      {!session && showIntro && <IntroScreen onDone={() => { setShowIntro(false); setShowLanding(false); }} />}
      {!session && !showIntro && showLanding && <LandingPage onEnter={() => setShowLanding(false)} />}
      {!session && !showIntro && !showLanding && (
        <LoginFlow
          onSuccess={handleLoginSuccess}
          onSuperAdmin={() => setShowSuperAdmin(true)}
          initialClubCode={linkClubCode ?? undefined}
        />
      )}
      {session?.role === "club_admin" && <AdminView session={session} onLogout={handleLogout} />}
      {session && session.role !== "club_admin" && <PlayerView session={session} onLogout={handleLogout} />}
    </>
  );
}

export default SpinloopApp;
